import { Client } from "@storyboard/web-client";
import { setupAmpli } from "./models/TrackingStateProvider";

export type ConfigData = {
  baseDomain: string;
  tusUrl: string;
  graphqlUrl: string;
  graphqlApiKey: string;
  sessionRecordingLength: number;
  gaKey: string;
  env: string;
  trackingKey?: string;
  debugMode?: boolean;
  buildVersion?: string;

  buildSha?: string;
  buildRef?: string;
  buildTags?: string[];
  buildAuthor?: string;
  buildDate?: string;
  buildMessage?: string;
  webRc?: string;
  webRelease?: string;
  releaseTag?: number;
};

export type FlagData = {
  debugMode?: boolean;
  recordingDebug?: boolean;
  sqlEditor?: boolean;
  allowInboundCalls?: boolean;
  limitedMemberRole?: boolean;
  showDebugMenu?: boolean;
  allowLocalUserSettings?: boolean;
  vadThreshold?: number;
  readOnlyMode?: boolean;
  disallowElectronUpgrade?: boolean;
  toolsTab?: boolean;
  handsFreePlayback?: boolean;
  browserCantGetToken?: boolean;
  boostCallAudio?: boolean;
  clearAllUnread?: boolean;
  inActiveTelemetry?: boolean;
  bootstrapWarmupFeedLimit?: number;
  unreadDebounceMs?: number;

  [key: string]: unknown;
};

const commitRef = import.meta?.env?.VITE_VERCEL_GIT_COMMIT_REF ?? "commit-ref-not-available";
const commitShaw = import.meta?.env?.VITE_VERCEL_GIT_COMMIT_SHA ?? "commit-shaw-not-available";
const branchUrl = import.meta?.env?.VITE_VERCEL_BRANCH_URL ?? "branch-url-not-available";
const pullRequestId = import.meta?.env?.VITE_VERCEL_GIT_PULL_REQUEST_ID ?? "pull-request-id-not-available";
const buildVersion = `${commitRef}|${commitShaw}|${branchUrl}|${pullRequestId}`;

// tags output
// svs/rc-211.3     svs/release-211  web/rc-145.6     web/rc-145.7     web/rc-145.8

const tags = import.meta.env.VITE_BUILD_TAGS ?? "";
const parsedTags = tags.split(" ").filter(Boolean);

const latestWebRcTag = parsedTags
  .filter((tag) => tag.startsWith("web/rc-"))
  .sort()
  .pop();

const latestWebReleaseTag = parsedTags
  .filter((tag) => tag.startsWith("web/release-"))
  .sort()
  .pop();

const nearestWebReleaseTag = import.meta.env.VITE_NEAREST_WEB_RELEASE_TAG ?? "";
const nearestWebRcTag = import.meta.env.VITE_NEAREST_WEB_RC_TAG ?? "";

const latestTagNumberOnly =
  Number(latestWebReleaseTag?.replace("web/release-", "")) ||
  Number(latestWebRcTag?.replace("web/rc-", "")) ||
  Number(nearestWebReleaseTag?.replace("web/release-", "")) ||
  Number(nearestWebRcTag?.replace("web/rc-", "")) ||
  0;

export const config: ConfigData = {
  baseDomain: import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BASE_DOMAIN,
  graphqlUrl: import.meta.env.VITE_APPSYNC_API_URL || import.meta.env.VITE_GRAPHQL_URL,
  graphqlApiKey: import.meta.env.VITE_APPSYNC_API_KEY || import.meta.env.VITE_GRAPHQL_API_KEY,
  tusUrl: import.meta.env.VITE_TUS_URL,
  sessionRecordingLength: Number(import.meta.env.VITE_SESSION_RECORDING_LENGTH),
  gaKey: import.meta.env.VITE_GA_KEY,
  env: import.meta.env.MODE,
  trackingKey: import.meta.env.VITE_TRACKING_KEY ?? "",
  debugMode: import.meta.env.VITE_DEBUG_MODE === "true" || import.meta.env.VITE_DEBUG === "true",
  buildVersion,
  buildSha: import.meta.env.VITE_BUILD_SHA || import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  buildRef: import.meta.env.VITE_VERCEL_GIT_COMMIT_REF,
  buildAuthor: import.meta.env.VITE_VERCEL_GIT_COMMIT_AUTHOR_NAME,
  buildMessage: import.meta.env.VITE_VERCEL_GIT_COMMIT_MESSAGE,
  buildTags: parsedTags,
  buildDate: import.meta.env.VITE_BUILD_DATE,
  webRc: latestWebRcTag,
  webRelease: latestWebReleaseTag,
  releaseTag: latestTagNumberOnly,
};

const debugFromUrl = new URLSearchParams(window.location.search).get("debug");

if (debugFromUrl === "true") {
  config.debugMode = true;
} else if (debugFromUrl === "false") {
  config.debugMode = false;
}

export const flags: FlagData = {
  debugMode: config.debugMode,
  recordingDebug: false,
  sqlEditor: config.debugMode,
  allowInboundCalls: config.debugMode,
  unreadDebounceMs: 2000,
};

type FlagChangeListener = (flags: FlagData) => void;
const flagChangeListeners: FlagChangeListener[] = [];

export function onFlagsChanged(listener: FlagChangeListener): () => void {
  flagChangeListeners.push(listener);
  return () => {
    const idx = flagChangeListeners.indexOf(listener);
    if (idx >= 0) flagChangeListeners.splice(idx, 1);
  };
}

export function notifyFlagChange(): void {
  for (const listener of flagChangeListeners) {
    listener(flags);
  }
}

export const ampli = setupAmpli(config.trackingKey);

export const client = new Client({
  baseDomain: config.baseDomain,
  logout: (returnTo?: string) => {
    ampli.client.setUserId(null);
    if (returnTo) {
      window.location.href = `${config.baseDomain}/auth/logout?returnTo=${returnTo}`;
    } else {
      window.location.href = `${config.baseDomain}/auth/logout`;
    }
  },
  logoutLink: (returnTo?: string) => {
    return `${config.baseDomain}/auth/logout${returnTo ? `?returnTo=${returnTo}` : ""}`;
  },
  login: () => {
    const dataLayer = typeof (window as any).dataLayer !== "undefined" ? (window as any).dataLayer : null;
    if (window && dataLayer) {
      dataLayer.push({
        event: {
          name: "authScreen",
          value: "Auth0 App: SMS",
        },
      });
    }
    ampli.authScreen({ "Auth0 App": "SMS" });
    const origin = window.location.origin;
    const appPath = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
    const returnTo = `${origin}/authSuccess?returnTo=${appPath}`;
    window.location.href = `${config.baseDomain}/auth/login?returnTo=${returnTo}`;
  },
});
